export type DashboardPaymentOrigin = 'reserva' | 'sesion' | 'cafeteria' | 'administrativo';

export interface DashboardPayment {
  id: number;
  code: string;
  paidAt: string;
  day: string;
  amount: string;
  paymentType: string;
  origin: DashboardPaymentOrigin;
  sourceCode: string | null;
  customer: string | null;
  branch: string | null;
}

interface FinancialBucket {
  count: number;
  cents: number;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_INSTANT_RE = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,6})?(Z|[+-]\d{2}:\d{2})$/;
const MONEY_RE = /^\d+(?:\.\d{1,2})?$/;
const ORIGINS = new Set<DashboardPaymentOrigin>(['reserva', 'sesion', 'cafeteria', 'administrativo']);
const ORIGIN_LABELS: Record<DashboardPaymentOrigin, string> = {
  reserva: 'Reservas',
  sesion: 'Sesiones',
  cafeteria: 'Cafetería',
  administrativo: 'Administrativo',
};

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === 'string';

const isCalendarDate = (value: string): boolean => {
  if (!DATE_RE.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day;
};

const isIsoInstant = (value: string): boolean => {
  const match = ISO_INSTANT_RE.exec(value);
  if (!match || !isCalendarDate(match[1])) return false;
  const [, , hours, minutes, seconds, zone] = match;
  if (Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) > 59) return false;
  if (zone !== 'Z') {
    const [offsetHours, offsetMinutes] = zone.slice(1).split(':').map(Number);
    if (offsetHours > 14 || offsetMinutes > 59 || (offsetHours === 14 && offsetMinutes !== 0)) return false;
  }
  return !Number.isNaN(Date.parse(value));
};

export const boliviaDateStamp = (instant: Date = new Date()): string => {
  if (Number.isNaN(instant.getTime())) throw new Error('Fecha inválida.');
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/La_Paz', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(item => item.type === type)?.value;
  const year = part('year');
  const month = part('month');
  const day = part('day');
  if (!year || !month || !day) throw new Error('Fecha inválida.');
  return `${year}-${month}-${day}`;
};

const toCents = (value: string): number => {
  if (!MONEY_RE.test(value)) throw new Error('Respuesta financiera inválida.');
  const [units, fraction = ''] = value.split('.');
  const cents = Number(units) * 100 + Number(fraction.padEnd(2, '0'));
  if (!Number.isSafeInteger(cents)) throw new Error('Respuesta financiera inválida.');
  return cents;
};

const money = (cents: number): string => `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;

export const parseDashboardPaymentRows = (rows: unknown[]): DashboardPayment[] => rows.map(row => {
  if (!row || typeof row !== 'object' || Array.isArray(row)) throw new Error('Respuesta financiera inválida.');
  const item = row as Record<string, unknown>;
  if (!Number.isInteger(item.id) || Number(item.id) <= 0
    || typeof item.codigo !== 'string' || !item.codigo
    || typeof item.fecha !== 'string' || !isIsoInstant(item.fecha)
    || typeof item.fecha_dia !== 'string' || !isCalendarDate(item.fecha_dia)
    || typeof item.monto !== 'string'
    || typeof item.tipo_pago !== 'string' || !item.tipo_pago.trim()
    || typeof item.origen !== 'string' || !ORIGINS.has(item.origen as DashboardPaymentOrigin)
    || !isNullableString(item.reserva_codigo)
    || !isNullableString(item.cliente)
    || !isNullableString(item.sucursal)) {
    throw new Error('Respuesta financiera inválida.');
  }
  if (boliviaDateStamp(new Date(item.fecha)) !== item.fecha_dia) {
    throw new Error('Respuesta financiera inválida.');
  }
  toCents(item.monto);
  return {
    id: Number(item.id), code: item.codigo, paidAt: item.fecha, day: item.fecha_dia,
    amount: item.monto, paymentType: item.tipo_pago, origin: item.origen as DashboardPaymentOrigin,
    sourceCode: item.reserva_codigo, customer: item.cliente, branch: item.sucursal,
  };
});

export const buildDashboardFinancials = (payments: DashboardPayment[]) => {
  let totalCents = 0;
  const payers = new Set<string>();
  const days = new Map<string, number>();
  const origins = new Map<DashboardPaymentOrigin, FinancialBucket>();
  const paymentTypes = new Map<string, FinancialBucket>();

  payments.forEach(payment => {
    const cents = toCents(payment.amount);
    totalCents += cents;
    if (!Number.isSafeInteger(totalCents)) throw new Error('Respuesta financiera inválida.');
    if (payment.customer) payers.add(payment.customer);
    days.set(payment.day, (days.get(payment.day) || 0) + cents);
    const origin = origins.get(payment.origin) || { count: 0, cents: 0 };
    origins.set(payment.origin, { count: origin.count + 1, cents: origin.cents + cents });
    const type = paymentTypes.get(payment.paymentType) || { count: 0, cents: 0 };
    paymentTypes.set(payment.paymentType, { count: type.count + 1, cents: type.cents + cents });
  });

  const byAmountThenLabel = <T extends { amount: string; label: string }>(a: T, b: T) =>
    toCents(b.amount) - toCents(a.amount) || a.label.localeCompare(b.label);

  return {
    total: money(totalCents),
    average: money(payments.length ? Math.round(totalCents / payments.length) : 0),
    paymentCount: payments.length,
    uniquePayers: payers.size,
    byDay: Array.from(days.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([date, cents]) => ({ date, amount: money(cents) })),
    byOrigin: Array.from(origins.entries()).map(([key, value]) => ({
      key, label: ORIGIN_LABELS[key], count: value.count, amount: money(value.cents),
    })).sort(byAmountThenLabel),
    byPaymentType: Array.from(paymentTypes.entries()).map(([label, value]) => ({
      label, count: value.count, amount: money(value.cents),
    })).sort(byAmountThenLabel),
  };
};
